/**
 * Term legend: specialized terms in an assistant reply are underlined in
 * place; hovering shows a one-line definition, and clicking pins the card open
 * with a short conversation about that term.
 *
 * Definitions come from the API (`/threads/:id/legend`), asked for once a text
 * block has finished streaming in this session.  Blocks that were already
 * complete when they mounted — a reopened thread — only read stored legends,
 * so scrolling through history never generates anything.  Legends are keyed by
 * a hash of the block's text, the same one the server stores them under.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Chat, useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { useAuiState, useMessage } from '@assistant-ui/react';
import { api, apiUrl, getApiToken } from '@/services/api';
import { rehypeTerms } from './termMarking';

export { rehypeTerms };

export interface LegendTerm {
  term: string;
  definition: string;
  kind: 'general' | 'project';
  inferred?: boolean;
}

// ── Store ────────────────────────────────────────────────────────────────────

const legends = new Map<string, Map<string, LegendTerm[]>>(); // threadId → text key → terms
const threadLoads = new Map<string, Promise<void>>();
const requests = new Set<string>(); // `${threadId}:${key}` already asked for
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

async function textKey(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest).slice(0, 8), (b) => b.toString(16).padStart(2, '0')).join('');
}

function loadThread(threadId: string): Promise<void> {
  let p = threadLoads.get(threadId);
  if (!p) {
    p = api
      .get<{ legends: Record<string, LegendTerm[]> }>(`/chat/threads/${threadId}/legends`)
      .then(({ legends: stored }) => {
        const m = legends.get(threadId) ?? new Map();
        for (const [k, terms] of Object.entries(stored)) m.set(k, terms);
        legends.set(threadId, m);
        notify();
      })
      .catch(() => { threadLoads.delete(threadId); });
    threadLoads.set(threadId, p);
  }
  return p;
}

function requestLegend(threadId: string, key: string, text: string): void {
  const id = `${threadId}:${key}`;
  if (requests.has(id)) return;
  requests.add(id);
  api
    .post<{ key: string; terms: LegendTerm[] }>(`/chat/threads/${threadId}/legend`, { text }, 120_000)
    .then(({ terms }) => {
      const m = legends.get(threadId) ?? new Map();
      m.set(key, terms);
      legends.set(threadId, m);
      notify();
    })
    .catch(() => { /* a missing legend just means no underlines */ });
}

/**
 * The legend for one text block.  `generate` is true once the block has
 * finished streaming in this session; otherwise only stored legends are read.
 */
export function useLegend(threadId: string | null, text: string, generate: boolean): { terms: LegendTerm[]; key: string | null } {
  const [key, setKey] = useState<string | null>(null);
  const [, bump] = useState(0);

  // An empty text (a block still streaming) has no legend yet — skip hashing it.
  useEffect(() => {
    if (!text) { setKey(null); return; }
    let live = true;
    textKey(text).then((k) => { if (live) setKey(k); });
    return () => { live = false; };
  }, [text]);

  useEffect(() => {
    const l = () => bump((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  useEffect(() => {
    if (!threadId || !key) return;
    loadThread(threadId).then(() => {
      if (generate && !legends.get(threadId)?.has(key)) requestLegend(threadId, key, text);
    });
  }, [threadId, key, generate, text]);

  const terms = (threadId && key && legends.get(threadId)?.get(key)) || EMPTY;
  return { terms, key };
}

const EMPTY: LegendTerm[] = [];

/** The `span` renderer react-markdown uses with `rehypeTerms`. */
export function makeTermSpan(terms: LegendTerm[], threadId: string | null, legendKey: string | null) {
  return function TermSpan({ node: _node, children, ...props }: any) {
    const idx = props['data-term-index'];
    const term = idx != null ? terms[Number(idx)] : undefined;
    if (!term || !threadId || !legendKey) return <span {...props}>{children}</span>;
    return <TermMark term={term} threadId={threadId} legendKey={legendKey}>{children}</TermMark>;
  };
}

// ── Underline + card ─────────────────────────────────────────────────────────

const OPEN_DELAY_MS = 220;
const CLOSE_DELAY_MS = 140;

function TermMark({ term, threadId, legendKey, children }: { term: LegendTerm; threadId: string; legendKey: string; children: ReactNode }) {
  const anchor = useRef<HTMLSpanElement>(null);
  const [mode, setMode] = useState<'closed' | 'peek' | 'pinned'>('closed');
  const [shown, setShown] = useState(false); // fades the underline in when the term arrives
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const clear = () => window.clearTimeout(timer.current);
  const peek = () => { clear(); if (mode === 'closed') timer.current = window.setTimeout(() => setMode('peek'), OPEN_DELAY_MS); };
  const leave = () => { clear(); if (mode !== 'pinned') timer.current = window.setTimeout(() => setMode('closed'), CLOSE_DELAY_MS); };
  useEffect(() => clear, []);

  const line = term.kind === 'project' ? 'decoration-dashed' : 'decoration-dotted';
  return (
    <>
      <span
        ref={anchor}
        onMouseEnter={peek}
        onMouseLeave={leave}
        onClick={() => { clear(); setMode('pinned'); }}
        className={`cursor-help underline underline-offset-[3px] decoration-1 transition-[text-decoration-color] duration-500 hover:decoration-zinc-500 ${line} ${
          shown ? 'decoration-zinc-400' : 'decoration-transparent'
        }`}
      >
        {children}
      </span>
      {mode !== 'closed' && anchor.current && (
        <TermCard
          anchor={anchor.current}
          term={term}
          threadId={threadId}
          legendKey={legendKey}
          pinned={mode === 'pinned'}
          onEnter={clear}
          onLeave={leave}
          onPin={() => setMode('pinned')}
          onClose={() => setMode('closed')}
        />
      )}
    </>
  );
}

const CARD_WIDTH = 336;
const GAP = 6;

function TermCard({
  anchor, term, threadId, legendKey, pinned, onEnter, onLeave, onPin, onClose,
}: {
  anchor: HTMLElement; term: LegendTerm; threadId: string; legendKey: string; pinned: boolean;
  onEnter: () => void; onLeave: () => void; onPin: () => void; onClose: () => void;
}) {
  const card = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const place = useCallback(() => {
    const r = anchor.getBoundingClientRect();
    const h = card.current?.offsetHeight ?? 0;
    const below = r.bottom + GAP;
    const top = below + h > window.innerHeight - 8 && r.top - GAP - h > 8 ? r.top - GAP - h : below;
    const left = Math.min(Math.max(8, r.left), window.innerWidth - CARD_WIDTH - 8);
    setPos({ top, left });
  }, [anchor]);

  useLayoutEffect(place, [place, pinned]);

  useEffect(() => {
    const onScroll = () => (pinned ? place() : onClose());
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e: MouseEvent) => {
      if (!pinned) return;
      const t = e.target as Node;
      if (!card.current?.contains(t) && !anchor.contains(t)) onClose();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [pinned, place, onClose, anchor]);

  const label = term.kind === 'general' ? 'General' : term.inferred ? 'Inferred from this thread' : 'This thread';

  return createPortal(
    <div
      ref={card}
      role="dialog"
      aria-label={term.term}
      onMouseEnter={onEnter}
      onMouseLeave={pinned ? undefined : onLeave}
      onClick={pinned ? undefined : onPin}
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: CARD_WIDTH }}
      className="fixed z-50 rounded-lg border border-zinc-200 bg-white text-meta shadow-lg animate-fade-in"
    >
      <div className="px-3.5 pb-3 pt-2.5">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-medium text-zinc-900 [overflow-wrap:anywhere]">{term.term}</span>
          <span className="shrink-0 text-xs text-zinc-400">{label}</span>
        </div>
        <p className="mt-1 leading-relaxed text-zinc-600">{term.definition}</p>
      </div>
      {pinned ? (
        <TermChat term={term} threadId={threadId} legendKey={legendKey} onResize={place} />
      ) : (
        <div className="border-t border-zinc-100 px-3.5 py-1.5 text-xs text-zinc-400">Click to ask about it</div>
      )}
    </div>,
    document.body,
  );
}

// ── Conversation about one term ──────────────────────────────────────────────

/** One conversation per term per block, kept for the session. */
const chats = new Map<string, Chat<any>>();
/** The grounding each conversation sends, refreshed on render (the Chat outlives any one render). */
const chatContext = new Map<string, { term: LegendTerm; sourceText: string; userText: string }>();

function textOf(content: any): string {
  return Array.isArray(content)
    ? content.filter((p: any) => p?.type === 'text' && typeof p.text === 'string').map((p: any) => p.text).join('\n\n')
    : '';
}

function TermChat({ term, threadId, legendKey, onResize }: { term: LegendTerm; threadId: string; legendKey: string; onResize: () => void }) {
  const message = useMessage();
  const sourceText = useMemo(() => textOf(message.content), [message.content]);
  // The user message that opened this turn: the nearest one before this reply.
  const userText = useAuiState((s: any) => {
    const msgs: any[] = s.thread?.messages ?? [];
    const at = msgs.findIndex((m) => m.id === message.id);
    for (let i = at - 1; i >= 0; i--) if (msgs[i].role === 'user') return textOf(msgs[i].content);
    return '';
  });

  const chatId = `${threadId}:${legendKey}:${term.term}`;
  chatContext.set(chatId, { term, sourceText, userText });
  const chat = useMemo(() => {
    let c = chats.get(chatId);
    if (!c) {
      c = new Chat({
        id: chatId,
        transport: new DefaultChatTransport({
          api: apiUrl(`/chat/threads/${threadId}/term-chat`, true),
          headers: async (): Promise<Record<string, string>> => {
            const token = await getApiToken();
            return token ? { Authorization: `Bearer ${token}` } : {};
          },
          body: () => chatContext.get(chatId) ?? {},
        }),
      });
      chats.set(chatId, c);
    }
    return c;
  }, [chatId, threadId]);

  const { messages, sendMessage, status, error } = useChat({ chat });
  const [draft, setDraft] = useState('');
  const busy = status === 'submitted' || status === 'streaming';
  const scroller = useRef<HTMLDivElement>(null);
  const lastQuestion = useRef<HTMLParagraphElement>(null);
  const questions = messages.filter((m) => m.role === 'user').length;

  // A new question goes to the top of the scroll area, so the answer reads
  // from its start as it streams in below it.
  useEffect(() => {
    const box = scroller.current, q = lastQuestion.current;
    if (box && q) box.scrollTo({ top: q.offsetTop - box.offsetTop - 8 });
  }, [questions]);
  useEffect(() => { onResize(); }, [messages, onResize]);

  const send = () => {
    const q = draft.trim();
    if (!q || busy) return;
    setDraft('');
    void sendMessage({ text: q });
  };

  return (
    <div className="border-t border-zinc-100">
      {messages.length > 0 && (
        <div ref={scroller} className="max-h-72 space-y-2.5 overflow-y-auto px-3.5 py-2.5">
          {messages.map((m, i) =>
            m.role === 'user' ? (
              <p key={m.id} ref={i === messages.map((x) => x.role).lastIndexOf('user') ? lastQuestion : undefined} className="text-zinc-500">{textOf(m.parts)}</p>
            ) : (
              <div key={m.id} className="prose prose-sm prose-zinc max-w-none text-meta leading-relaxed prose-headings:mb-1 prose-headings:mt-3 prose-headings:text-meta prose-headings:font-semibold prose-headings:text-zinc-900 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-p:my-1.5 prose-p:text-zinc-800 prose-li:text-zinc-800 prose-code:text-zinc-800 prose-code:bg-zinc-100 prose-code:rounded prose-code:px-1 prose-code:text-xs prose-code:before:content-none prose-code:after:content-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{textOf(m.parts)}</ReactMarkdown>
              </div>
            ),
          )}
          {status === 'submitted' && <p className="text-zinc-400">…</p>}
          {error && <p className="text-red-600">Couldn't get an answer. Try again.</p>}
        </div>
      )}
      <div className="px-3 pb-3 pt-2">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={messages.length ? 'Ask a follow-up' : 'Ask about this term'}
          className="w-full rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-meta text-zinc-800 placeholder:text-zinc-400 focus:border-zinc-400 focus:outline-none focus:ring-0"
        />
      </div>
    </div>
  );
}
